// Smart Sash Admin Panel - useRfidTagManagement
// State + URL-persisted filters for the RFID Tags admin page.

import {
  InstitutionBasicDTO,
  RfidAssignedFilter,
  RfidTagListItem,
  RfidTagsQueryParams,
  RfidTagStatusFilter,
} from '@/models';
import { rfidAuthService, rfidService } from '@/services/api';
import {
  useGroupStore,
  useInstitutionStore,
  useLabStore,
  useRfidTagStore,
  useUIStore,
  useUserStore,
} from '@/store';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/services/auth';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 100;
const TEXT_DEBOUNCE_MS = 300;

const LOOKUP_PAGE_SIZE = 100;

const STATUS_VALUES: ReadonlyArray<RfidTagStatusFilter> = [
  'Active',
  'Inactive',
  'Expired',
  'Unassigned',
];

export interface RfidTagFilters {
  status: RfidTagStatusFilter | '';
  assigned: RfidAssignedFilter;
  serialNumber: string;
  labId: string;
  groupId: string;
  institutionId: string;
  userName: string;
  userEmail: string;
  /** Date-only string in YYYY-MM-DD form. Converted to end-of-day UTC ISO when sent. */
  expiresBefore: string;
}

const EMPTY_FILTERS: RfidTagFilters = {
  status: '',
  assigned: 'any',
  serialNumber: '',
  labId: '',
  groupId: '',
  institutionId: '',
  userName: '',
  userEmail: '',
  expiresBefore: '',
};

function parseAssigned(value: string | null): RfidAssignedFilter {
  if (value === 'true') return 'assigned';
  if (value === 'false') return 'unassigned';
  return 'any';
}

function parseStatus(value: string | null): RfidTagStatusFilter | '' {
  if (value && (STATUS_VALUES as ReadonlyArray<string>).includes(value)) {
    return value as RfidTagStatusFilter;
  }
  return '';
}

function readFiltersFromSearch(params: URLSearchParams): RfidTagFilters {
  return {
    status: parseStatus(params.get('status')),
    assigned: parseAssigned(params.get('assigned')),
    serialNumber: params.get('serialNumber') ?? '',
    labId: params.get('labId') ?? '',
    groupId: params.get('groupId') ?? '',
    institutionId: params.get('institutionId') ?? '',
    userName: params.get('userName') ?? '',
    userEmail: params.get('userEmail') ?? '',
    expiresBefore: params.get('expiresBefore') ?? '',
  };
}

function readPageFromSearch(params: URLSearchParams): number {
  const raw = params.get('page');
  if (raw === null || raw === '') return 1;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed >= 1 ? Math.floor(parsed) : 1;
}

function readPageSizeFromSearch(params: URLSearchParams): number {
  const raw = params.get('pageSize');
  if (raw === null || raw === '') return DEFAULT_PAGE_SIZE;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_PAGE_SIZE;
  return Math.min(100, Math.max(1, Math.floor(parsed)));
}

function expiresBeforeToIso(dateOnly: string): string | undefined {
  if (!dateOnly) return undefined;
  // Treat the picked date as the cutoff: include the entire day in UTC.
  const iso = new Date(`${dateOnly}T23:59:59Z`).toISOString();
  return Number.isNaN(Date.parse(iso)) ? undefined : iso;
}

function buildQuery(
  filters: RfidTagFilters,
  page: number,
  pageSize: number
): RfidTagsQueryParams {
  const query: RfidTagsQueryParams = { page, pageSize };
  if (filters.status) query.status = filters.status;
  if (filters.assigned === 'assigned') query.assigned = true;
  if (filters.assigned === 'unassigned') query.assigned = false;
  if (filters.serialNumber) query.serialNumber = filters.serialNumber;
  if (filters.labId) query.labId = filters.labId;
  if (filters.groupId) query.groupId = filters.groupId;
  if (filters.institutionId) query.institutionId = filters.institutionId;
  if (filters.userName) query.userName = filters.userName;
  if (filters.userEmail) query.userEmail = filters.userEmail;
  const iso = expiresBeforeToIso(filters.expiresBefore);
  if (iso) query.expiresBefore = iso;
  return query;
}

export const useRfidTagManagement = () => {
  const { tags, pagination, isLoading, error, errorStatus, fetchTags, clearError } =
    useRfidTagStore();
  const { labs, fetchLabs } = useLabStore();
  const { groups, fetchGroups } = useGroupStore();
  const { institutions, fetchInstitutions } = useInstitutionStore();
  const { users, isLoading: isLoadingUsers, fetchUsers } = useUserStore();
  const { addNotification } = useUIStore();
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const canToggleActive = hasPermission('Rfid.CreateCard');
  const canCreateCard = hasPermission('Rfid.CreateCard');
  // Anyone who can list institutions should see the full picker — not just HawkinsAdmin.
  const canListInstitutions = hasPermission('Institution.ViewAll', 'Institution.View');

  const [searchParams, setSearchParams] = useSearchParams();
  const [unlinkingCardId, setUnlinkingCardId] = useState<string | null>(null);
  const [tagToAssign, setTagToAssign] = useState<RfidTagListItem | null>(null);
  const [isAssigningCard, setIsAssigningCard] = useState(false);
  const [togglingCardId, setTogglingCardId] = useState<string | null>(null);
  const [tagToDeactivate, setTagToDeactivate] = useState<RfidTagListItem | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreatingCard, setIsCreatingCard] = useState(false);

  const filters = useMemo(() => readFiltersFromSearch(searchParams), [searchParams]);
  const currentPage = readPageFromSearch(searchParams);
  const pageSize = readPageSizeFromSearch(searchParams);

  // Institutions exposed to the Create modal. Anyone with institution-list permission gets
  // the real fetched list; otherwise we synthesize a single entry from auth so the picker
  // still has a usable option (institution-admin scenario).
  const modalInstitutions = useMemo<InstitutionBasicDTO[]>(() => {
    if (canListInstitutions && institutions.length > 0) return institutions;
    if (!user?.institutionId) return [];
    return [{ id: user.institutionId, name: user.institutionName || user.institutionId }];
  }, [canListInstitutions, institutions, user?.institutionId, user?.institutionName]);
  const hasActiveFilters =
    !!filters.status ||
    filters.assigned !== 'any' ||
    !!filters.serialNumber ||
    !!filters.labId ||
    !!filters.groupId ||
    !!filters.institutionId ||
    !!filters.userName ||
    !!filters.userEmail ||
    !!filters.expiresBefore;

  // Load lookup data for filter dropdowns.
  useEffect(() => {
    fetchLabs(1, LOOKUP_PAGE_SIZE);
    fetchGroups(1, LOOKUP_PAGE_SIZE);
    fetchInstitutions(1, LOOKUP_PAGE_SIZE);
  }, [fetchLabs, fetchGroups, fetchInstitutions]);

  // Debounce when free-text filters are active (serial/user inputs); immediate otherwise.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const query = buildQuery(filters, currentPage, pageSize);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    const hasFreeText = !!filters.serialNumber || !!filters.userName || !!filters.userEmail;
    if (hasFreeText) {
      debounceRef.current = setTimeout(() => {
        fetchTags(query);
      }, TEXT_DEBOUNCE_MS);
      return () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
      };
    }
    fetchTags(query);
    return undefined;
  }, [fetchTags, currentPage, pageSize, filters]);

  const updateSearchParams = useCallback(
    (mutator: (next: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutator(next);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setFilter = useCallback(
    <K extends keyof RfidTagFilters>(key: K, value: RfidTagFilters[K]) => {
      updateSearchParams((next) => {
        // Filter changes always reset to page 1.
        next.delete('page');
        if (key === 'assigned') {
          const v = value as RfidAssignedFilter;
          if (v === 'any') next.delete('assigned');
          else next.set('assigned', v === 'assigned' ? 'true' : 'false');
          return;
        }
        if (!value) {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      });
    },
    [updateSearchParams]
  );

  const resetFilters = useCallback(() => {
    updateSearchParams((next) => {
      next.delete('status');
      next.delete('assigned');
      next.delete('serialNumber');
      next.delete('labId');
      next.delete('groupId');
      next.delete('institutionId');
      next.delete('userName');
      next.delete('userEmail');
      next.delete('expiresBefore');
      next.delete('page');
    });
  }, [updateSearchParams]);

  const setCurrentPage = useCallback(
    (page: number) => {
      updateSearchParams((next) => {
        if (page <= 1) next.delete('page');
        else next.set('page', String(page));
      });
    },
    [updateSearchParams]
  );

  const handlePageSizeChange = useCallback(
    (size: number) => {
      const clamped = Math.min(100, Math.max(1, Math.floor(size)));
      updateSearchParams((next) => {
        next.delete('page');
        if (clamped === DEFAULT_PAGE_SIZE) next.delete('pageSize');
        else next.set('pageSize', String(clamped));
      });
    },
    [updateSearchParams]
  );

  const retry = useCallback(() => {
    fetchTags(buildQuery(filters, currentPage, pageSize));
  }, [fetchTags, filters, currentPage, pageSize]);

  const performSetActive = useCallback(
    async (tag: RfidTagListItem, nextActive: boolean) => {
      setTogglingCardId(tag.cardId);
      try {
        await rfidService.setCardActive(tag.cardId, nextActive);
        addNotification({
          type: 'success',
          message: nextActive ? 'Card enabled' : 'Card disabled',
          duration: 2000,
        });
        await fetchTags(buildQuery(filters, currentPage, pageSize));
      } catch (err: unknown) {
        addNotification({
          type: 'error',
          message: getErrorMessage(err, 'Failed to update card state'),
          duration: 3000,
        });
        // On 404 the card disappeared between fetch and toggle — refresh.
        const status = (err as { response?: { status?: number } } | null)?.response?.status;
        if (status === 404) {
          await fetchTags(buildQuery(filters, currentPage, pageSize));
        }
      } finally {
        setTogglingCardId(null);
      }
    },
    [fetchTags, filters, currentPage, pageSize, addNotification]
  );

  const handleToggleActive = useCallback(
    (tag: RfidTagListItem) => {
      // Reactivation: flip directly. Deactivation: require confirmation.
      if (!tag.isActive) {
        performSetActive(tag, true);
        return;
      }
      setTagToDeactivate(tag);
    },
    [performSetActive]
  );

  const closeDeactivateModal = useCallback(() => {
    setTagToDeactivate(null);
  }, []);

  const handleConfirmDeactivate = useCallback(async () => {
    if (!tagToDeactivate) return;
    const tag = tagToDeactivate;
    setTagToDeactivate(null);
    await performSetActive(tag, false);
  }, [tagToDeactivate, performSetActive]);

  const handleAssignClick = useCallback(
    (tag: RfidTagListItem) => {
      setTagToAssign(tag);
      // Lazy-load users only when the assign modal is first needed.
      if (users.length === 0 && !isLoadingUsers) {
        fetchUsers();
      }
    },
    [users.length, isLoadingUsers, fetchUsers]
  );

  const closeAssignModal = useCallback(() => {
    setTagToAssign(null);
  }, []);

  const handleConfirmAssign = useCallback(
    async (userId: string, expiresAt?: string) => {
      if (!tagToAssign) return;
      setIsAssigningCard(true);
      try {
        await rfidAuthService.assignCard({
          userId,
          tag: tagToAssign.serialNumber,
          expiresAt,
        });
        addNotification({
          type: 'success',
          message: 'RFID card assigned successfully',
          duration: 2000,
        });
        setTagToAssign(null);
        await fetchTags(buildQuery(filters, currentPage, pageSize));
      } catch (err: unknown) {
        addNotification({
          type: 'error',
          message: getErrorMessage(err, 'Failed to assign RFID card'),
          duration: 3000,
        });
      } finally {
        setIsAssigningCard(false);
      }
    },
    [tagToAssign, fetchTags, filters, currentPage, pageSize, addNotification]
  );

  const openCreateModal = useCallback(() => {
    setIsCreateModalOpen(true);
    // Lazy-load users so the optional assignment picker has data ready.
    if (users.length === 0 && !isLoadingUsers) {
      fetchUsers();
    }
  }, [users.length, isLoadingUsers, fetchUsers]);

  const closeCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  const handleCreateCard = useCallback(
    async (
      institutionId: string,
      serialNumber: string,
      assignment: { userId: string; expiresAt?: string } | null
    ) => {
      setIsCreatingCard(true);
      try {
        await rfidService.createCard({ institutionId, serialNumber });
        if (assignment) {
          // The assign endpoint resolves the card by serial, so the just-created
          // card can be linked right away.
          await rfidAuthService.assignCard({
            userId: assignment.userId,
            tag: serialNumber,
            expiresAt: assignment.expiresAt,
          });
        }
        addNotification({
          type: 'success',
          message: assignment ? 'RFID card created and assigned' : 'RFID card created',
          duration: 2000,
        });
        setIsCreateModalOpen(false);
        await fetchTags(buildQuery(filters, currentPage, pageSize));
      } catch (err: unknown) {
        addNotification({
          type: 'error',
          message: getErrorMessage(err, 'Failed to create RFID card'),
          duration: 3000,
        });
      } finally {
        setIsCreatingCard(false);
      }
    },
    [fetchTags, filters, currentPage, pageSize, addNotification]
  );

  const handleUnlinkCard = useCallback(
    async (tag: RfidTagListItem) => {
      if (!tag.user || !tag.assignment) return;
      setUnlinkingCardId(tag.cardId);
      try {
        await rfidAuthService.revokeAssignedCard({
          institutionId: tag.institutionId,
          userId: tag.user.userId,
          cardId: tag.cardId,
        });
        addNotification({
          type: 'success',
          message: 'Card unlinked successfully',
          duration: 2000,
        });
        await fetchTags(buildQuery(filters, currentPage, pageSize));
      } catch (err: unknown) {
        addNotification({
          type: 'error',
          message: getErrorMessage(err, 'Failed to unlink card'),
          duration: 3000,
        });
      } finally {
        setUnlinkingCardId(null);
      }
    },
    [fetchTags, filters, currentPage, pageSize, addNotification]
  );

  return {
    tags,
    pagination,
    isLoading,
    error,
    errorStatus,
    labs,
    groups,
    institutions,
    filters,
    hasActiveFilters,
    currentPage,
    pageSize,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    canChooseInstitution: canListInstitutions,
    canToggleActive,
    canCreateCard,
    modalInstitutions,
    defaultFilters: EMPTY_FILTERS,
    unlinkingCardId,
    users,
    isLoadingUsers,
    tagToAssign,
    isAssigningCard,
    togglingCardId,
    tagToDeactivate,
    isCreateModalOpen,
    isCreatingCard,
    setFilter,
    resetFilters,
    setCurrentPage,
    handlePageSizeChange,
    clearError,
    retry,
    handleUnlinkCard,
    handleAssignClick,
    handleConfirmAssign,
    closeAssignModal,
    handleToggleActive,
    handleConfirmDeactivate,
    closeDeactivateModal,
    openCreateModal,
    closeCreateModal,
    handleCreateCard,
  };
};
